import { useState, useEffect, Fragment } from 'react';
import {
  Settings2,
  Plus,
  Pencil,
  Trash2,
  Play,
  Undo2,
  Eye,
  Loader2,
  ToggleLeft,
  ToggleRight,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { DashboardLayout } from '../components/DashboardLayout';
import { useEntityStore } from '../store';
import { categorizationRulesAPI } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { getErrorMessage } from '../lib/errors';

const CATEGORIES = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'supplier_payment', label: 'Supplier Payment' },
  { value: 'intercompany', label: 'Intercompany' },
  { value: 'tax_payment', label: 'Tax Payment' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'personal', label: 'Personal' },
  { value: 'loan', label: 'Loan' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking_com', label: 'Booking.com' },
  { value: 'direct_booking', label: 'Direct Booking' },
  { value: 'bank_fee', label: 'Bank Fee' },
  { value: 'internal_transfer', label: 'Internal Transfer' },
  { value: 'other', label: 'Other' },
];

const categoryLabel = (val) => CATEGORIES.find(c => c.value === val)?.label || val;

const categoryColor = {
  revenue: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  supplier_payment: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  intercompany: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  tax_payment: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  subscription: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  personal: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
  loan: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  other: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
};

const emptyRule = {
  name: '',
  scope: { type: 'global', entity_id: null, bank_account_id: null },
  conditions: {
    counterparty_contains: '',
    counterparty_regex: '',
    description_contains: '',
    description_regex: '',
    reference_contains: '',
    amount_min: '',
    amount_max: '',
    direction: '',
    currency: '',
    source: '',
  },
  action: { category: 'other', counterparty_normalized: '' },
  overwrite_policy: 'uncategorized_only',
  priority: 0,
  enabled: true,
};

export function CategorizationRulesPageContent() {
  const { entities } = useEntityStore();
  const [rules, setRules] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(emptyRule);

  // Backfill state
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [isPreviewingBackfill, setIsPreviewingBackfill] = useState(false);
  const [isApplyingBackfill, setIsApplyingBackfill] = useState(false);
  const [backfillFilters, setBackfillFilters] = useState({
    entity_id: '', date_from: '', date_to: '',
  });

  // Revert state
  const [revertPreview, setRevertPreview] = useState(null);
  const [revertRunId, setRevertRunId] = useState('');
  const [isPreviewingRevert, setIsPreviewingRevert] = useState(false);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(null);

  const fetchData = async () => {
    try {
      const [rulesRes, diagRes] = await Promise.all([
        categorizationRulesAPI.list(),
        categorizationRulesAPI.diagnostics(),
      ]);
      setRules(rulesRes.data);
      setDiagnostics(diagRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Pick up prefilled rule from "Create rule from transaction" flow
  useEffect(() => {
    const stored = sessionStorage.getItem('prefilled_rule');
    if (stored) {
      sessionStorage.removeItem('prefilled_rule');
      try {
        const prefilled = JSON.parse(stored);
        setForm({
          name: prefilled.name || '',
          scope: prefilled.scope || emptyRule.scope,
          conditions: {
            counterparty_contains: prefilled.conditions?.counterparty_contains || '',
            counterparty_regex: prefilled.conditions?.counterparty_regex || '',
            description_contains: prefilled.conditions?.description_contains || '',
            description_regex: prefilled.conditions?.description_regex || '',
            reference_contains: prefilled.conditions?.reference_contains || '',
            amount_min: prefilled.conditions?.amount_min ?? '',
            amount_max: prefilled.conditions?.amount_max ?? '',
            direction: prefilled.conditions?.direction || '',
            currency: prefilled.conditions?.currency || '',
            source: prefilled.conditions?.source || '',
          },
          action: {
            category: prefilled.action?.category || 'other',
            counterparty_normalized: prefilled.action?.counterparty_normalized || '',
          },
          overwrite_policy: prefilled.overwrite_policy || 'uncategorized_only',
          priority: prefilled.priority ?? 0,
          enabled: prefilled.enabled !== false,
        });
        setShowForm(true);
      } catch { /* ignore parse errors */ }
    }
  }, []);

  const handleSave = async () => {
    const payload = {
      ...form,
      conditions: {
        ...form.conditions,
        counterparty_contains: form.conditions.counterparty_contains || null,
        counterparty_regex: form.conditions.counterparty_regex || null,
        description_contains: form.conditions.description_contains || null,
        description_regex: form.conditions.description_regex || null,
        reference_contains: form.conditions.reference_contains || null,
        amount_min: form.conditions.amount_min !== '' ? Number(form.conditions.amount_min) : null,
        amount_max: form.conditions.amount_max !== '' ? Number(form.conditions.amount_max) : null,
        direction: form.conditions.direction || null,
        currency: form.conditions.currency || null,
        source: form.conditions.source || null,
      },
      action: {
        ...form.action,
        counterparty_normalized: form.action.counterparty_normalized || null,
      },
      scope: {
        ...form.scope,
        entity_id: form.scope.entity_id || null,
        bank_account_id: form.scope.bank_account_id || null,
      },
      priority: Number(form.priority) || 0,
    };

    try {
      if (editingRule) {
        await categorizationRulesAPI.update(editingRule.id, payload);
        toast.success('Rule updated');
      } else {
        const res = await categorizationRulesAPI.create(payload);
        toast.success(`Rule created (${res.data.dry_run_match_count} existing transactions would match)`);
      }
      setShowForm(false);
      setEditingRule(null);
      setForm(emptyRule);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name || '',
      scope: rule.scope || emptyRule.scope,
      conditions: {
        counterparty_contains: rule.conditions?.counterparty_contains || '',
        counterparty_regex: rule.conditions?.counterparty_regex || '',
        description_contains: rule.conditions?.description_contains || '',
        description_regex: rule.conditions?.description_regex || '',
        reference_contains: rule.conditions?.reference_contains || '',
        amount_min: rule.conditions?.amount_min ?? '',
        amount_max: rule.conditions?.amount_max ?? '',
        direction: rule.conditions?.direction || '',
        currency: rule.conditions?.currency || '',
        source: rule.conditions?.source || '',
      },
      action: {
        category: rule.action?.category || 'other',
        counterparty_normalized: rule.action?.counterparty_normalized || '',
      },
      overwrite_policy: rule.overwrite_policy || 'uncategorized_only',
      priority: rule.priority ?? 0,
      enabled: rule.enabled !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (rule) => {
    try {
      await categorizationRulesAPI.delete(rule.id);
      toast.success('Rule disabled');
      setDeleteDialog(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handlePreviewBackfill = async () => {
    setIsPreviewingBackfill(true);
    try {
      const res = await categorizationRulesAPI.previewBackfill({
        entity_id: backfillFilters.entity_id || undefined,
        date_from: backfillFilters.date_from || undefined,
        date_to: backfillFilters.date_to || undefined,
      });
      setBackfillPreview(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsPreviewingBackfill(false);
    }
  };

  const handleApplyBackfill = async () => {
    setIsApplyingBackfill(true);
    try {
      const res = await categorizationRulesAPI.applyBackfill({
        entity_id: backfillFilters.entity_id || undefined,
        date_from: backfillFilters.date_from || undefined,
        date_to: backfillFilters.date_to || undefined,
      });
      toast.success(`Backfill applied: ${res.data.applied} categorized, ${res.data.skipped} skipped`);
      setBackfillPreview(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsApplyingBackfill(false);
    }
  };

  const handlePreviewRevert = async () => {
    if (!revertRunId.trim()) return;
    setIsPreviewingRevert(true);
    try {
      const res = await categorizationRulesAPI.previewRevert(revertRunId.trim());
      setRevertPreview(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsPreviewingRevert(false);
    }
  };

  const handleApplyRevert = async () => {
    try {
      const res = await categorizationRulesAPI.applyRevert(revertPreview.run_id);
      toast.success(`Reverted ${res.data.reverted} transactions`);
      setRevertPreview(null);
      setRevertRunId('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const updateCondition = (field, value) => {
    setForm(f => ({ ...f, conditions: { ...f.conditions, [field]: value } }));
  };

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Categorization Rules</h1>
            <p className="text-muted-foreground mt-1">Auto-categorize bank transactions by counterparty, amount, and direction</p>
          </div>
          <Button onClick={() => { setShowForm(true); setEditingRule(null); setForm(emptyRule); }}>
            <Plus className="h-4 w-4 mr-2" /> New Rule
          </Button>
        </div>

        {/* Diagnostics Card */}
        {diagnostics && (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Enabled Rules</p>
                <p className="text-2xl font-bold font-mono mt-0.5">{diagnostics.enabled_rules}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Auto-categorized (7d)</p>
                <p className="text-2xl font-bold font-mono text-green-600 mt-0.5">{diagnostics.auto_categorized_7d}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Manual (7d)</p>
                <p className="text-2xl font-bold font-mono mt-0.5">{diagnostics.manual_categorized_7d}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Uncategorized</p>
                <p className="text-2xl font-bold font-mono text-orange-600 mt-0.5">{diagnostics.uncategorized_count}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Rule Conflicts</p>
                <p className={cn(
                  "text-2xl font-bold font-mono mt-0.5",
                  diagnostics.rule_conflicts > 0 ? "text-amber-600" : "text-muted-foreground"
                )}>
                  {diagnostics.rule_conflicts ?? 0}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Last Backfill</p>
                <p className="text-sm font-mono mt-0.5">
                  {diagnostics.last_backfill
                    ? `${diagnostics.last_backfill.applied_count} txns`
                    : 'None'}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rule Form */}
        {showForm && (
          <Card>
            <CardHeader>
              <CardTitle>{editingRule ? 'Edit Rule' : 'Create Rule'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. HMRC Tax Payments" />
                </div>
                <div>
                  <label className="text-sm font-medium">Category (action)</label>
                  <Select value={form.action.category} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, category: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Conditions */}
              <div className="border rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-muted-foreground">Match Conditions (all non-empty conditions must match)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Counterparty contains</label>
                    <Input value={form.conditions.counterparty_contains} onChange={e => updateCondition('counterparty_contains', e.target.value)} placeholder="e.g. HMRC" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Counterparty regex</label>
                    <Input value={form.conditions.counterparty_regex} onChange={e => updateCondition('counterparty_regex', e.target.value)} placeholder="e.g. (?i)amazon.*aws" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Description contains</label>
                    <Input value={form.conditions.description_contains} onChange={e => updateCondition('description_contains', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Reference contains</label>
                    <Input value={form.conditions.reference_contains} onChange={e => updateCondition('reference_contains', e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Direction</label>
                    <Select value={form.conditions.direction || 'any'} onValueChange={v => updateCondition('direction', v === 'any' ? '' : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="inbound">Inbound (+)</SelectItem>
                        <SelectItem value="outbound">Outbound (-)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Currency</label>
                    <Input value={form.conditions.currency} onChange={e => updateCondition('currency', e.target.value)} placeholder="e.g. GBP" />
                  </div>
                </div>
              </div>

              {/* Scope + Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Scope</label>
                  <Select value={form.scope.type} onValueChange={v => setForm(f => ({ ...f, scope: { ...f.scope, type: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global</SelectItem>
                      <SelectItem value="entity">Entity</SelectItem>
                      <SelectItem value="account">Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(form.scope.type === 'entity' || form.scope.type === 'account') && (
                  <div>
                    <label className="text-sm font-medium">Entity</label>
                    <Select value={form.scope.entity_id || ''} onValueChange={v => setForm(f => ({ ...f, scope: { ...f.scope, entity_id: v } }))}>
                      <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                      <SelectContent>
                        {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium">Priority</label>
                  <Input type="number" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} />
                </div>
              </div>

              {/* Overwrite policy */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Overwrite policy</label>
                  <Select value={form.overwrite_policy} onValueChange={v => setForm(f => ({ ...f, overwrite_policy: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uncategorized_only">Only uncategorized (safe default)</SelectItem>
                      <SelectItem value="always">Always (overwrite existing)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Normalized counterparty</label>
                  <Input value={form.action.counterparty_normalized} onChange={e => setForm(f => ({ ...f, action: { ...f.action, counterparty_normalized: e.target.value } }))} placeholder="Optional clean name" />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave}>{editingRule ? 'Update' : 'Create'} Rule</Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setEditingRule(null); setForm(emptyRule); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rules List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" /> Rules ({rules.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : rules.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No rules yet. Create one to start auto-categorizing transactions.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Conditions</th>
                      <th>Category</th>
                      <th>Scope</th>
                      <th>Priority</th>
                      <th>Matches (7d)</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map(rule => (
                      <tr key={rule.id}>
                        <td className="font-medium">{rule.name}</td>
                        <td className="text-xs max-w-[250px]">
                          {rule.conditions?.counterparty_contains && (
                            <span className="block">Counterparty: "{rule.conditions.counterparty_contains}"</span>
                          )}
                          {rule.conditions?.description_contains && (
                            <span className="block">Description: "{rule.conditions.description_contains}"</span>
                          )}
                          {rule.conditions?.direction && (
                            <span className="block">Direction: {rule.conditions.direction}</span>
                          )}
                          {rule.conditions?.currency && (
                            <span className="block">Currency: {rule.conditions.currency}</span>
                          )}
                        </td>
                        <td>
                          <Badge className={categoryColor[rule.action?.category] || categoryColor.other}>
                            {categoryLabel(rule.action?.category)}
                          </Badge>
                        </td>
                        <td className="text-xs">
                          {rule.scope?.type || 'global'}
                          {rule.scope?.entity_id && (
                            <span className="block text-muted-foreground">{rule.scope.entity_id}</span>
                          )}
                        </td>
                        <td className="font-mono">{rule.priority}</td>
                        <td className="font-mono">{rule.match_count_7d ?? 0}</td>
                        <td>
                          {rule.enabled ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Active</Badge>
                          ) : (
                            <Badge variant="outline">Disabled</Badge>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(rule)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => setDeleteDialog(rule)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backfill Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" /> Backfill: Apply Rules to Existing Transactions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">Entity (optional)</label>
                <Select value={backfillFilters.entity_id || 'all'} onValueChange={v => setBackfillFilters(f => ({ ...f, entity_id: v === 'all' ? '' : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All entities</SelectItem>
                    {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Date from</label>
                <Input type="date" value={backfillFilters.date_from} onChange={e => setBackfillFilters(f => ({ ...f, date_from: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Date to</label>
                <Input type="date" value={backfillFilters.date_to} onChange={e => setBackfillFilters(f => ({ ...f, date_to: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePreviewBackfill} disabled={isPreviewingBackfill}>
                {isPreviewingBackfill ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview
              </Button>
              {backfillPreview && backfillPreview.total_matches > 0 && (
                <Button onClick={handleApplyBackfill} disabled={isApplyingBackfill}>
                  {isApplyingBackfill ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Apply ({backfillPreview.total_matches} transactions)
                </Button>
              )}
            </div>

            {/* Preview Results */}
            {backfillPreview && (
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex gap-4 text-sm">
                  <span>Total matches: <strong>{backfillPreview.total_matches}</strong></span>
                  {backfillPreview.conflicts > 0 && (
                    <span className="text-amber-600">Conflicts: <strong>{backfillPreview.conflicts}</strong></span>
                  )}
                </div>
                {backfillPreview.per_rule?.map(pr => (
                  <div key={pr.rule_id} className="border-t pt-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge className={categoryColor[pr.proposed_category] || categoryColor.other}>
                        {categoryLabel(pr.proposed_category)}
                      </Badge>
                      <span className="font-medium">{pr.rule_name}</span>
                      <span className="text-muted-foreground">({pr.match_count} matches)</span>
                    </div>
                    {pr.samples?.length > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground space-y-0.5 pl-4">
                        {pr.samples.map((s, i) => (
                          <div key={i}>
                            {s.counterparty || 'N/A'} | {s.description} | {s.currency} {s.amount} | {s.current_category} -&gt; {s.proposed_category}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revert Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5" /> Revert a Backfill Run
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Run ID (from backfill result)"
                value={revertRunId}
                onChange={e => setRevertRunId(e.target.value)}
                className="max-w-md"
              />
              <Button variant="outline" onClick={handlePreviewRevert} disabled={isPreviewingRevert || !revertRunId.trim()}>
                {isPreviewingRevert ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview Revert
              </Button>
            </div>
            {revertPreview && (
              <div className="border rounded-lg p-4 space-y-2">
                <p className="text-sm">Transactions to revert: <strong>{revertPreview.count}</strong></p>
                {revertPreview.items?.slice(0, 10).map((item, i) => (
                  <div key={i} className="text-xs text-muted-foreground">
                    {item.counterparty || 'N/A'} | {item.current_category} -&gt; {item.revert_to}
                  </div>
                ))}
                {revertPreview.count > 0 && (
                  <Button variant="destructive" size="sm" onClick={handleApplyRevert}>
                    <Undo2 className="h-4 w-4 mr-2" /> Apply Revert ({revertPreview.count})
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete confirmation */}
        <AlertDialog open={!!deleteDialog} onOpenChange={open => { if (!open) setDeleteDialog(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disable rule "{deleteDialog?.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                The rule will be disabled (not deleted). Transactions already categorized by this rule will keep their category.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDelete(deleteDialog)}>Disable</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </div>
  );
}

export function CategorizationRulesPage() {
  return (
    <DashboardLayout>
      <CategorizationRulesPageContent />
    </DashboardLayout>
  );
}
